import React, { useState, useRef, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import imageSrc from "../../assets/images/mobile-app.png";
import imageSrcIcon1 from "../../assets/images/icon-companies.svg";
import imageSrcIcon2 from "../../assets/images/icon-labor.svg";
import imageSrcIcon3 from "../../assets/images/icon-products-services.svg";

// Define the style type
type TransformStyle = {
    transform: string;
    opacity: number; // We'll fade it in too
};

// Initial/Neutral State
const NEUTRAL_TRANSFORM = 'translateY(0px)';
const COMMON_STYLE = {
    transition: 'transform 0.6s ease-out, opacity 0.6s ease-out', // Slower transition for a smooth reveal
    willChange: 'transform, opacity',
};

const Services: React.FC = () => {
  const { t } = useTranslation();

  // Ref for the entire section (to track scroll position)
  const servicesRef = useRef<HTMLElement>(null);

  // State for the image's dynamic style
  const [imageStyle, setImageStyle] = useState<TransformStyle>({ transform: NEUTRAL_TRANSFORM, opacity: 0 });

  useLayoutEffect(() => {

    const handleScroll = () => {
      const currentSection = servicesRef.current;
      if (!currentSection) {
        return;
      }

      const rect = currentSection.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // Calculate how far the section has moved into the viewport
      // 0 = section top is at viewport bottom (just visible)
      // 1 = section bottom is at viewport bottom (fully visible)
      const scrollProgress = 1 - (rect.top / viewportHeight);

      // Clamp the progress between 0 and 1
      const clampedProgress = Math.min(1, Math.max(0, scrollProgress));


      // --- Reveal Effect Logic ---

      // Start position: hidden 150px below final position, and fully transparent
      const START_OFFSET = 150;

      // Calculate the current offset (goes from 150 to 0)
      const currentOffset = START_OFFSET * (1 - clampedProgress);

      // Calculate the opacity (goes from 0 to 1)
      const currentOpacity = clampedProgress;


      // --- Reset logic for when element is out of view (going back up) ---

      // If the component is mostly out of view (e.g., less than 10% visible)
      if (clampedProgress < 0.1) {
          setImageStyle({ transform: `translateY(${START_OFFSET}px)`, opacity: 0 });
          return;
      }

      // Set the dynamic style
      setImageStyle({
          transform: `translateY(${currentOffset}px)`,
          opacity: currentOpacity
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);


  return (
    <section id="services"
      ref={servicesRef} // Attach ref to the section
      className="banner neumorphic-container rounded-0 p-5 mb-0"
    >
      <div className="container col-lg-12">
        <h2 className="text-center">{t("services.title")}</h2>
        <p className="text-center">{t("services.subtitle")}</p>
      </div>
      <div className="row justify-content-center align-items-center">
        <div className="col-12 col-lg-6 d-flex justify-content-center align-items-center">
          <div className="neumorphic-container oval oval-sm d-flex justify-content-center
           align-items-center my-4">
            <div className="neumorphic-container  oval oval-xs overflow-hidden ">
              <img src={imageSrc}
               className="col-12"
                alt="logo-white"
                style={{ ...COMMON_STYLE, ...imageStyle }}
              />
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-6">
          <div className="neumorphic-container mb-4 d-flex justify-content-center align-items-center p-3">
            <div className="me-3">
              <div className="testimonial-image-container neumorphic-container p-3 rounded-4 overflow-hidden">
                <img className="testimonial-image" src={imageSrcIcon1} alt="Services icon"/>
              </div>
            </div>
            <div>
              <h3 className="gradient-text">{t("services.companies.title")}</h3>
              <p>{t("services.companies.description")}</p>
            </div>
          </div>
          <div className="neumorphic-container mb-4 d-flex justify-content-center align-items-center p-3">
            <div className="me-3">
              <div className="testimonial-image-container neumorphic-container p-3 rounded-4 overflow-hidden">
                <img className="testimonial-image" src={imageSrcIcon2} alt="Services icon"/>
              </div>
            </div>
            <div>
              <h3 className="gradient-text">{t("services.labor.title")}</h3>
              <p>{t("services.labor.description")}</p>
            </div>
          </div>
          <div className="neumorphic-container mb-4 d-flex justify-content-center align-items-center p-3">
            <div className="me-3">
              <div className="testimonial-image-container neumorphic-container p-3 rounded-4 overflow-hidden">
                <img className="testimonial-image" src={imageSrcIcon3} alt="Services icon"/>
              </div>
            </div>
            <div>
            <h3 className="gradient-text">{t("services.products.title")}</h3>
            <p>{t("services.products.description")}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
export default Services;
